import { styled, Typography } from '@mui/material';
import { TransactionType } from './Types';

const TableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 16px',
  display: 'grid',
  gridTemplateColumns: '400px 1fr 1fr 1fr 1fr 140px ',
  columnGap: '15px',
  overflowX: 'auto',
  whiteSpace: 'nowrap',

  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 10,
    gridTemplateColumns: '380px 0.5fr 1.5fr 1fr 1fr 1fr',
  },
  [theme.breakpoints.down('lg')]: {
    padding: '32px 0px',
    gap: 15,
    gridTemplateColumns: '340px 1fr 1.5fr',
    gridTemplateRows: 'repeat(2,1fr)',
    '.whiteSpace': {
      display: 'none',
    },
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    marginBottom: '60px',
    marginTop: '0px',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    div: {
      width: 'auto',
    },
  },
}));

const CreditWalletTableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 0',
  display: 'grid',
  gridTemplateColumns: '400px 1fr 1fr 1fr 1fr 140px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 10,
    gridTemplateColumns: '380px 0.5fr 1.5fr 1fr 1fr 1fr',
  },
  [theme.breakpoints.down('lg')]: {
    padding: '32px 0px',
    gap: 15,
    gridTemplateColumns: '340px 1fr 1.5fr',
    gridTemplateRows: 'repeat(2,1fr)',
    '.whiteSpace': {
      display: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    div: {
      width: 'auto',
    },
  },
}));

const NavigationBtnContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '20px',
  alignItems: 'stretch',
  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

const NavigationBtn = styled('div')<{
  isOverLimit?: boolean;
  isHighlighted?: boolean;
}>(({ theme, isOverLimit, isHighlighted }) => ({
  padding: '24px',
  display: 'grid',
  gridTemplateColumns: '48px 1fr',
  alignItems: 'center',
  gap: '16px',
  border: '1px solid',
  borderRadius: '12px',
  borderColor: isOverLimit ? theme.palette.error.main : theme.palette.divider,
  backgroundColor: isOverLimit
    ? 'rgba(211, 47, 47, 0.04)'
    : isHighlighted
    ? theme.palette.primary.light
    : theme.palette.background.paper,
  color: isOverLimit ? theme.palette.error.main : 'inherit',
  height: '100%',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: '40px 1fr',
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '36px 1fr',
    padding: '16px',
    gap: '12px',
  },
}));

export const IconWrapper = styled('div')<{ isOverLimit?: boolean }>(({ theme, isOverLimit }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: isOverLimit ? 'rgba(211, 47, 47, 0.1)' : theme.palette.primary.light,
  color: isOverLimit ? theme.palette.error.main : theme.palette.primary.main,
  [theme.breakpoints.down('xl')]: {
    width: '40px',
    height: '40px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
  },
}));

export const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: 1.2,
  marginTop: '4px',
  fontFeatureSettings: '"tnum" on, "lnum" on',
  [theme.breakpoints.down('xl')]: {
    fontSize: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
  },
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  letterSpacing: '0.02em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

export const CurrencyBadge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
  fontSize: '12px',
  fontWeight: 500,
  padding: '2px 6px',
  borderRadius: '4px',
  marginLeft: '6px',
}));

const FilterSectionContainer = styled('div')`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr;
  grid-gap: 24px;
  margin-bottom: 30px;
`;

const FilterSection = styled('div')<{ hasTwoDivisions?: boolean }>(({ theme, hasTwoDivisions }) => ({
  height: '130px',
  padding: '20px',
  display: 'grid',
  gridTemplateColumns: hasTwoDivisions ? '1fr 1fr' : '1fr 1fr 2fr',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.border.grey}`,
  backgroundColor: theme.palette.primary.light,
}));

const FilterLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.purple,
  fontSize: '20px',
  fontWeight: 500,
}));

const DebitedIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  width: 28,
  height: 28,
  borderRadius: 20,
  marginRight: 10,
}));

const CreditedIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  width: 28,
  height: 28,
  borderRadius: 20,
  marginRight: 10,
}));

const IconStyle = { width: '33px', height: '33px' };

const TransactionStatus = styled(Typography)(() => ({
  // backgroundColor: theme.palette.background.grey,
  paddingBlock: 5,
  width: 120,
  marginInline: 'auto',
  textAlign: 'center',
  fontWeight: 500,
  borderRadius: 2,
}));

const AmountTypo = styled(Typography)<{ type: TransactionType }>(({ theme }) => ({
  color: theme.palette.common.black,
  fontWeight: 600,
}));

export {
  TableActionContainer,
  CreditWalletTableActionContainer,
  NavigationBtnContainer,
  NavigationBtn,
  FilterSectionContainer,
  FilterSection,
  FilterLabel,
  IconStyle,
  DebitedIcon,
  CreditedIcon,
  TransactionStatus,
  AmountTypo,
};
